import { generateRoutes, isRoleInPermissions } from './utilities';
import { ROUTES } from './routes.config';

export class Router {
	constructor(routesConfig = ROUTES) {
		const { routes, validPaths } = generateRoutes(routesConfig);

		this.routes = routes;
		this.validPaths = validPaths;

		// TIP: Use this when you need to regenerate unit-test routes config data
		// console.log(JSON.stringify(routes, null, 2));
		// console.log(validPaths);
	}

	/**
	 * Utility function to flatten all routes into an single array
	 *
	 * @returns {array}
	 */
	_flattenRoutes() {
		const routes = [];
		this.routes.map((route) => {
			routes.push(route);
			if (!!route.children) {
				routes.push(route.children);
			}
		});
		return routes.flat();
	}

	/**
	 * Loosely validates that the path we're trying to navigate to is valid (except the root /)
	 *
	 * @param {string} toPath - The path we're attempting to navigate to
	 * @returns {boolean}
	 */
	isValidRoute(toPath) {
		let validRoute = toPath !== '/' ? false : true;
		if (toPath !== '/') {
			for (let i = 0; i < this.validPaths.length; i++) {
				const splitPath = this.validPaths[i].split('/');
				splitPath.shift(); // Removes the first / match

				// For multi level routes, Router only ever checks for 1st level match
				if (splitPath.length > 0) {
					validRoute = toPath.startsWith(`/${splitPath[0]}`);
				} else {
					validRoute = false;
				}
				if (validRoute) break;
			}
		}
		return validRoute;
	}

	/**
	 * Verifies that the user role exists for the given route
	 *
	 * @param {string} toPath - The path we're attempting to navigate to, to retrieve the actual route object
	 * @param {array} roles - List of the user roles
	 * @returns {boolean}
	 */
	canAccessRoute(toPath, roles) {
		const route = this.getRouteFromPath(toPath);
		if (Object.keys(route).length <= 0) return false;

		return isRoleInPermissions(roles, route.permissions);
	}

	/**
	 * Returns all available route objects
	 *
	 * @returns {array}
	 */
	getAllRoutes() {
		return this.routes;
	}

	/**
	 * Returns one or more routes starting with the given name value
	 *
	 * @param {string} name - The route name
	 * @returns {array}
	 */
	getRoutesFromName(name) {
		return this.routes.filter((route) => route.name.startsWith(name));
	}

	/**
	 * Returns a single route given a name
	 *
	 * @param {string} name - The route name
	 * @returns {object}
	 */
	getRouteFromName(name) {
		const finalRoute = this._flattenRoutes().find((route) => route.name === name);

		return finalRoute ? finalRoute : {};
	}

	/**
	 * Returns which route matches the given currentPath.
	 *
	 * The process to find the route is simple:
	 *  - Split the query with by "/"
	 *  - Find the match with less variables since:
	 *     - /example/:id/foo/:section is less of a fit then
	 *     - /example/:id/foo/details
	 *
	 * @param {string} currentPath - A path from windown.location.pathname
	 * @returns {object} - Empty when no match
	 */
	getRouteFromPath(currentPath) {
		const routes = this._flattenRoutes();
		const cpSplit = currentPath.split('/');

		const sortedRoutes = [...routes].sort((a, b) => {
			const aSplit = a.path.split('/');
			const bSplit = b.path.split('/');

			if (aSplit.length === bSplit.length) {
				for (let i = 0; i < aSplit.length; i++) {
					if (aSplit[i] !== bSplit[i]) {
						// TODO: might happen some conflict between :id and :section (mixing routes)
						return aSplit[i].startsWith(':') ? 1 : -1;
					}
				}
			}

			return bSplit.length - aSplit.length;
		});

		return (
			sortedRoutes.find((route) => {
				const routePathSplit = route.path.split('/').map((segment) => {
					if (segment.startsWith(':')) return new RegExp('.*');
					return new RegExp('^' + segment + '$');
				});
				return routePathSplit.every((segment, index) => segment.test(cpSplit[index]));
			}) ?? {}
		);
	}

	/**
	 * Gets the first route that has matching permissions for the given role
	 *
	 * @param {string} role - The current user role
	 * @returns {object} - Empty when no match
	 */
	getRouteForRedirect(roles) {
		let route = {};
		route = this._flattenRoutes().find((route) => {
			return !!!route.children && isRoleInPermissions(roles, route.permissions);
		});
		return route;
	}
}
